/**
 * Whether to render the team-profile link for a given league. Used to
 * be a hand-curated whitelist of leagues PM ships team pages for, but
 * that approach kept rotting: every new sport/esport (HoK, Chinese
 * Super League, R6 majors, hockey leagues PM later added) silently
 * lost its team links because nobody remembered to extend the set.
 *
 * Now we trust the backend: any non-empty league code earns a link.
 * Our `/sports/players/{league}/{slug}` route resolves the team via
 * gamma /teams (broader coverage than PM's web /teams URLs), and when
 * it can't find one the page renders a graceful fallback with an
 * external link to PM — never a 404 in the user's face.
 */
export function hasPlayerProfilePage(league: string | null | undefined): boolean {
  return !!league && String(league).trim().length > 0;
}

/**
 * Build a Polymarket-compatible team/player slug from a display name.
 *
 * PM does NOT transliterate — every non-ASCII-alphanumeric character is
 * dropped (treated as a separator). This means accented letters like ç,
 * ø, ñ become hyphens, not their ASCII counterparts.
 *
 *   "FK Bodø/Glimt"      → "fk-bod-glimt"     (ø dropped, / → -)
 *   "Barça Esports GC"   → "bar-a-esports-gc" (ç dropped between r and a)
 *   "St. Louis Cardinals" → "st-louis-cardinals"
 *   "Yasmine Kabbaj"     → "yasmine-kabbaj"
 */
export function playerSlug(name: string): string {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
